import { VehicleWithShortDriversOutput } from '@allocab/allocab-driver-shared-types';
import {
  BoltSolid,
  CalendarCheckRegular,
  CarSolid,
  DropletSolid,
  FlagSolid,
  GearSolid,
  MotorcycleSolid,
  UsersSolid,
} from '@allocab/allocab-icons';
import { getPanelNameLabelByKey } from '@allocab/allocab-shared-utils';

import { VehicleOptionsIndicator } from './VehicleOptionsIndicator';
import { Link } from '../../components/atomic/Link';
import { BadgeIndicator } from '../../components/BadgeIndicator';
import { LicensePlate } from '../../components/LicensePlate';
import { VehicleAssignButton } from '../../components/vehicle-assign-button/VehicleAssignButton';
import { t } from '../../services/translation/translation';
import { formatDate, parseDate } from '../../utils/date';
import { getEngineTypeText } from '../../utils/vehicle';

type Props = {
  vehicle: VehicleWithShortDriversOutput;
};

export const VehicleCard = ({ vehicle }: Props) => {
  return (
    <div className="max-w-full p-3 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700 space-y-1">
      <div className="grid grid-cols-[0.5fr,1fr,1fr] gap-1">
        <div className="flex items-center justify-center p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 space-x-2">
          <UsersSolid className="fill-current" width={13} height={13} />
          <div>{vehicle.drivers.length}</div>
        </div>
        <div className="flex items-center justify-center p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600">
          <Link to={`/vehicles/${vehicle.id}`}>
            <LicensePlate plateNumber={vehicle.licensePlate} />
          </Link>
        </div>
        <div className="flex items-center justify-center p-2 text-xs ">
          <BadgeIndicator variant={vehicle.validated ? 'green' : 'yellow'}>
            {vehicle.validated ? t('common.validated') : t('common.validating')}
          </BadgeIndicator>
        </div>
      </div>
      <div className="grid grid-cols-[1fr,1fr] gap-1">
        <div className="flex items-center justify-center p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 space-x-2 capitalize text-center">
          <div>
            <FlagSolid className="fill-current" width={13} height={13} />
          </div>
          <div>{`${vehicle.brand?.toLowerCase() ?? ''} ${vehicle.range?.toLowerCase() ?? ''}`}</div>
        </div>
        <div className="flex items-center justify-center p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 space-x-2">
          <div>
            {vehicle.panelKeys?.includes('MOTO') ? (
              <MotorcycleSolid className="fill-current" width={13} height={13} />
            ) : (
              <CarSolid className="fill-current" width={13} height={13} />
            )}
          </div>
          <div>{vehicle.panelKeys?.map(p => getPanelNameLabelByKey(p)).join(',') ?? '--'}</div>
        </div>
      </div>
      <div className="grid grid-cols-[1fr,1fr,1fr] gap-1">
        <div className="flex items-center justify-center p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 space-x-2 capitalize text-center">
          <div>
            {vehicle.engineType === 'DIESEL' || vehicle.engineType === 'GASOLINE' ? (
              <GearSolid className="fill-current" width={13} height={13} />
            ) : null}
            {vehicle.engineType === 'SIMPLE_HYBRID' ||
            vehicle.engineType === 'PLUGIN_HYBRID' ||
            vehicle.engineType === 'ELECTRIC' ? (
              <BoltSolid className="fill-current" width={13} height={13} />
            ) : null}
          </div>
          <div>{vehicle.engineType ? getEngineTypeText(vehicle.engineType) : '--'}</div>
        </div>
        <div className="flex items-center justify-center p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 space-x-2">
          <div>
            <CalendarCheckRegular className="fill-current" width={13} height={13} />
          </div>
          <div>
            {vehicle.firstDate ? formatDate(parseDate(vehicle.firstDate, 'DD-MM-YYYY'), t('date.date-short')) : '--'}
          </div>
        </div>
        <div className="flex items-center justify-center p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600 space-x-1 capitalize text-center">
          <div>
            <DropletSolid className="fill-current" width={13} height={13} />
          </div>
          <div>{vehicle.color?.toLowerCase() ?? '--'}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1">
        <div className="flex items-center justify-center p-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-600">
          <VehicleOptionsIndicator vehicle={vehicle} />
        </div>
        {vehicle.validated ? <VehicleAssignButton drivers={vehicle.drivers} vehicleId={vehicle.id} /> : null}
      </div>
    </div>
  );
};
